import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../config/axiosInterceptor"
import {toast} from "react-toastify"

const VideoModal = ({ isOpen, videoUrl, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        display: isOpen ? "flex" : "none",
        position: "fixed",
        zIndex: 1000,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "20px",
          maxWidth: "90%",
          maxHeight: "90%",
          position: "relative",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "rgba(0,0,0,0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            fontSize: "18px",
            cursor: "pointer",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          title="Close Video"
        >
          ×
        </button>

        {/* Video Player */}
        <video 
          controls 
          autoPlay
          style={{ 
            width: "100%", 
            height: "auto",
            maxHeight: "70vh",
            borderRadius: "8px"
          }}
          onError={(e) => {
            console.error("Video load error:", e);
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
          <source src={videoUrl} type="video/ogg" />
          Your browser does not support the video tag.
        </video>

        {/* Video Info */}
        <div style={{ 
          marginTop: "10px", 
          textAlign: "center",
          fontSize: "12px",
          color: "#666"
        }}>
          <i className="fa fa-info-circle" style={{ marginRight: "5px" }}></i>
          Use video controls to play, pause, and adjust volume
        </div>
      </div>
    </div>
  );
};

const Video = () => {
  const baseUrl = process.env.REACT_APP_BASEURL1;
  const [videos, setVideos] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [sortBy, setSortBy] = useState("createdAt");
  const [searchUsername, setSearchUsername] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // For controlled input
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [totalVideos, setTotalVideos] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams({
          page: currentPage,
          limit: entriesPerPage,
          type: sortBy,
          ...(searchUsername && { username: searchUsername })
        });
        
        const response = await axios.get( 
          `${process.env.REACT_APP_BASEURL4}/admin/video?${queryParams}`
        );
        
        setVideos(response.data.details || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalVideos(response.data.totalVideos || 0);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch videos");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, entriesPerPage, sortBy, searchUsername]);

  const handleDelete = async (videoId, username) => {
    if (window.confirm(`Are you sure you want to delete this video by ${username}? This action cannot be undone.`)) {
      try {
        setLoading(true);
        const response = await axiosInstance.delete(`${baseUrl}/admin/video/${videoId}`);
        
        if (response.data.success) {
          setVideos(videos.filter((video) => video._id !== videoId));
          setTotalVideos(prev => prev - 1);
          toast.success("Video deleted successfully");
        } else {
          toast.error(response.data.message || "Failed to delete video");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting video");
        console.error("Error deleting video:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchUsername(searchTerm.trim());
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchUsername("");
    setCurrentPage(1);
  };

  const handleView = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentVideoUrl(""); // Clear video URL on close
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
          <button onClick={() => paginate(i)} className="page-link">
            {i}
          </button>
        </li>
      );
    }

    return pageNumbers;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <section className="content-header">
        <h1>Video List</h1>
        <ol className="breadcrumb">
          <li>
            <Link to="/admin">
              <i className="fa fa-dashboard"></i> Home
            </Link>
          </li>
          <li className="active">Video List</li>
        </ol>
      </section>
      <section className="content">
        <div className="row">
          <div className="col-xs-12">
            <div className="box">
              <div className="box-body">
                {/* Search and Controls Row */}
                <div className="row" style={{ padding: "16px 0px", backgroundColor: "#f9f9f9", borderRadius: "5px", marginBottom: "20px" }}>
                  <div className="col-md-4">
                    <label htmlFor="search" style={{ fontWeight: "bold" }}>Search by Username:</label>
                    <form onSubmit={handleSearch} style={{ display: "flex", gap: "5px" }}>
                      <input
                        type="text"
                        id="search"
                        className="form-control"
                        placeholder="Enter username..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ height: "35px" }}
                      />
                      <button 
                        type="submit" 
                        className="btn btn-primary btn-sm"
                        disabled={loading}
                        style={{ height: "35px", minWidth: "60px" }}
                      >
                        {loading ? "..." : "Search"}
                      </button>
                      {searchUsername && (
                        <button 
                          type="button" 
                          className="btn btn-secondary btn-sm"
                          onClick={clearSearch}
                          style={{ height: "35px" }}
                        >
                          Clear
                        </button>
                      )}
                    </form>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="entries" style={{ fontWeight: "bold" }}>Entries per page:</label>
                    <select
                      id="entries"
                      className="form-control"
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                      style={{ height: "35px" }}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="sortBy" style={{ fontWeight: "bold" }}>Sort by:</label>
                    <select
                      id="sortBy"
                      className="form-control"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{ height: "35px" }}
                    >
                      <option value="createdAt">Date Created (Latest First)</option>
                      <option value="viewCount">View Count (Highest First)</option>
                    </select>
                  </div>
                </div>

                {/* Results Info */}
                <div className="row" style={{ marginBottom: "15px" }}>
                  <div className="col-md-12">
                    <div style={{ 
                      padding: "10px", 
                      backgroundColor: "#e8f4fd", 
                      borderRadius: "3px",
                      fontSize: "14px"
                    }}>
                      {searchUsername ? (
                        <span>
                          <strong>Search Results for "{searchUsername}":</strong> {totalVideos} video(s) found
                        </span>
                      ) : (
                        <span>
                          <strong>Total Videos:</strong> {totalVideos} | 
                          <strong> Page:</strong> {currentPage} of {totalPages} | 
                          <strong> Sorted by:</strong> {sortBy === 'createdAt' ? 'Date Created' : 'View Count'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <table className="table table-bordered table-striped table-hover">
                  <thead style={{ backgroundColor: "#f5f5f5" }}>
                    <tr>
                      <th style={{ width: "8%" }}>Sr.</th>
                      <th style={{ width: "20%" }}>User Info</th>
                      <th style={{ width: "15%" }}>Thumbnail</th>
                      <th style={{ width: "12%" }}>Views</th>
                      <th style={{ width: "15%" }}>Created Date</th>
                      <th style={{ width: "15%" }}>Description</th>
                      <th style={{ width: "15%" }}>Action</th>
                    </tr>
                  </thead>
                  {loading ? (
                    <tbody>
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                          <div style={{ fontSize: "16px", color: "#666" }}>
                            <i className="fa fa-spinner fa-spin" style={{ marginRight: "10px" }}></i>
                            Loading videos...
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : videos.length === 0 ? (
                    <tbody>
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                          <div style={{ fontSize: "16px", color: "#999" }}>
                            {searchUsername ? `No videos found for user "${searchUsername}"` : "No videos found"}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {videos.map((item, index) => {
                        const i = index + 1 + (currentPage - 1) * entriesPerPage;
                        return (
                          <tr key={item._id} style={{ verticalAlign: "middle" }}>
                            <td style={{ fontWeight: "bold" }}>{i}</td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {item.userInfo?.picture && (
                                  <img
                                    src={item.userInfo.picture}
                                    alt="User"
                                    style={{ 
                                      height: "30px", 
                                      width: "30px", 
                                      borderRadius: "50%",
                                      objectFit: "cover"
                                    }}
                                  />
                                )}
                                <div>
                                  <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                                    {item.userInfo?.username || "Unknown"}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#666" }}>
                                    {item.userInfo?.name || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <img
                                src={item.thumbnail}
                                alt="Thumbnail"
                                style={{ 
                                  height: "60px", 
                                  width: "80px", 
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "2px solid #ddd"
                                }}
                                onError={(e) => {
                                  e.target.src = "https://via.placeholder.com/80x60?text=No+Image";
                                }}
                              />
                            </td>
                            <td>
                              <span style={{ 
                                backgroundColor: "#e8f4fd", 
                                padding: "4px 8px", 
                                borderRadius: "12px",
                                fontSize: "13px",
                                fontWeight: "bold"
                              }}>
                                {Number(item.viewCount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {item.createdAtFormatted}
                            </td>
                            <td style={{ fontSize: "12px", maxWidth: "150px" }}>
                              <div style={{ 
                                overflow: "hidden", 
                                textOverflow: "ellipsis", 
                                whiteSpace: "nowrap" 
                              }}>
                                {item.description || "No description"}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "5px" }}>
                                <button
                                  className="btn btn-info btn-xs"
                                  onClick={() => handleView(item.video)}
                                  title="View Video"
                                  style={{ minWidth: "35px" }}
                                >
                                  <i className="fa fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-danger btn-xs"
                                  onClick={() => handleDelete(item._id, item.userInfo?.username)}
                                  title="Delete Video"
                                  disabled={loading}
                                  style={{ minWidth: "35px" }}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  )}
                </table>
                {/* Enhanced Pagination controls */}
                {totalPages > 1 && (
                  <div className="row">
                    <div className="col-md-12">
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center",
                        padding: "20px 0",
                        borderTop: "1px solid #eee",
                        marginTop: "20px"
                      }}>
                        {/* Pagination Info */}
                        <div style={{ fontSize: "14px", color: "#666" }}>
                          Showing {((currentPage - 1) * entriesPerPage) + 1} to{" "}
                          {Math.min(currentPage * entriesPerPage, totalVideos)} of{" "}
                          {totalVideos} videos
                        </div>

                        {/* Pagination Controls */}
                        <ul className="pagination" style={{ margin: "0" }}>
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(1)}
                              className="page-link"
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(currentPage - 1)}
                              className="page-link"
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa fa-angle-left"></i> Previous
                            </button>
                          </li>
                          
                          {renderPageNumbers()}
                          
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(currentPage + 1)}
                              className="page-link"
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              Next <i className="fa fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(totalPages)}
                              className="page-link"
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>

                        {/* Quick Jump */}
                        {totalPages > 5 && (
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ fontSize: "14px", color: "#666" }}>Go to page:</span>
                            <input
                              type="number"
                              min="1"
                              max={totalPages}
                              value={currentPage}
                              onChange={(e) => {
                                const page = parseInt(e.target.value);
                                if (page >= 1 && page <= totalPages) {
                                  paginate(page);
                                }
                              }}
                              style={{
                                width: "60px",
                                height: "30px",
                                textAlign: "center",
                                border: "1px solid #ddd",
                                borderRadius: "3px"
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ... */}
              </div>
            </div>
          </div>
          </div>
      </section>

      {/* Video Modal */}
      <VideoModal isOpen={isModalOpen} videoUrl={currentVideoUrl} onClose={closeModal} />
    </>
  );
};

export default Video;
